import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EnvelopeSimple, PaperPlaneTilt, CircleNotch, Eye, CheckCircle, Warning } from '@phosphor-icons/react';
import { API } from '@/config/api';

export default function RecruiterEodPanel() {
  const [loading, setLoading] = useState(true);
  const [liveMode, setLiveMode] = useState(false);
  const [rows, setRows] = useState([]);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewName, setPreviewName] = useState('');
  const [previewLoading, setPreviewLoading] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [overrideEmail, setOverrideEmail] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/recruiter-eod/preview`);
      setLiveMode(!!data?.live_mode);
      setRows(Array.isArray(data?.recruiters) ? data.recruiters : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load preview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPreview = useCallback(async (row) => {
    setPreviewLoading(row.recruiter_id);
    try {
      const res = await axios.get(`${API}/admin/recruiter-eod/preview/${row.recruiter_id}/html`);
      setPreviewHtml(typeof res.data === 'string' ? res.data : '');
      setPreviewName(`${row.recruiter_name} · ${row.intake_label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not render preview');
    } finally {
      setPreviewLoading(null);
    }
  }, []);

  const sendTest = useCallback(async (row) => {
    const to = overrideEmail.trim();
    setSendingId(row.recruiter_id);
    try {
      const { data } = await axios.post(`${API}/admin/recruiter-eod/send-test`, {
        recruiter_id: row.recruiter_id,
        to_email: to || undefined,
      });
      toast.success(`Test sent to ${data?.sent_to || to || 'admin'} — ${row.recruiter_name} payload`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSendingId(null);
    }
  }, [overrideEmail]);

  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="recruiter-eod-panel">
      <header className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-lg bg-indigo-100 flex items-center justify-center">
          <EnvelopeSimple size={24} weight="duotone" className="text-indigo-700" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400">Scheduled email</p>
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900 mt-1 leading-[1.1]" style={{ fontFamily: "'Libre Baskerville', serif" }}>
            Recruiter End-of-Day digest
          </h2>
          <p className="text-gray-500 text-[13px] mt-2 max-w-2xl leading-relaxed">
            Sent to every recruiter at <span className="font-semibold text-gray-700">9:00 PM Toronto time</span>, scoped to their most-recent intake. Preview mode is on by default; only your test sends land in real inboxes until you flip <code className="px-1 bg-gray-100 rounded">RECRUITER_EOD_LIVE=true</code>.
          </p>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border ${liveMode ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
          {liveMode ? 'LIVE' : 'Preview only'}
        </div>
      </header>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 max-w-xs">
          <label className="block text-[11px] font-semibold text-gray-600 mb-1">Send test to (optional)</label>
          <Input
            type="email"
            value={overrideEmail}
            onChange={(e) => setOverrideEmail(e.target.value)}
            placeholder="your.email@domain.com (defaults to your admin email)"
            className="h-9 text-xs"
            data-testid="recruiter-eod-override-email"
          />
        </div>
        <Button
          type="button"
          onClick={load}
          variant="outline"
          className="h-9 text-xs mt-5"
          data-testid="recruiter-eod-refresh"
        >
          {loading ? <CircleNotch size={12} className="mr-1.5 animate-spin" /> : null}
          Refresh
        </Button>
      </div>

      <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm">
        <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            Recruiters in scope <span className="text-gray-500 font-normal">({rows.length})</span>
          </h3>
          {!liveMode && rows.length > 0 && (
            <p className="text-[11px] text-amber-700 inline-flex items-center gap-1"><Warning size={11} weight="fill" /> No real recruiter emails will go out until Live mode is enabled.</p>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white border-b">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Recruiter</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Intake</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">Total</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">COE</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">EA Signed</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">Pre-Auth</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">Earning</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-500"><CircleNotch size={16} className="animate-spin inline mr-2" /> Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">No recruiter has students in a recent intake yet.</td></tr>
              )}
              {!loading && rows.map(r => (
                <tr key={r.recruiter_id} data-testid={`recruiter-eod-row-${r.recruiter_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{r.recruiter_name || '—'}</div>
                    <div className="text-[10.5px] text-gray-500">{r.recruiter_email || '(no email on file)'}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{r.intake_label}</td>
                  <td className="px-4 py-3 text-right text-gray-900 font-semibold">{r.metrics.total_students}</td>
                  <td className="px-4 py-3 text-right text-gray-800">{r.metrics.coe_received}</td>
                  <td className="px-4 py-3 text-right text-gray-800">{r.metrics.ea_signed}</td>
                  <td className="px-4 py-3 text-right text-gray-800">{r.metrics.preauth_done}</td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-800">{fmtMoney(r.metrics.estimated_earning)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => openPreview(r)}
                        disabled={previewLoading === r.recruiter_id}
                        className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] font-semibold rounded-md bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 disabled:opacity-50"
                        data-testid={`recruiter-eod-preview-${r.recruiter_id}`}
                        title="Preview the rendered email body"
                      >
                        {previewLoading === r.recruiter_id ? <CircleNotch size={10} className="animate-spin" /> : <><Eye size={11} weight="bold" /> Preview</>}
                      </button>
                      <button
                        type="button"
                        onClick={() => sendTest(r)}
                        disabled={sendingId === r.recruiter_id}
                        className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] font-semibold rounded-md bg-indigo-700 hover:bg-indigo-800 text-white disabled:opacity-50"
                        data-testid={`recruiter-eod-send-test-${r.recruiter_id}`}
                        title={overrideEmail ? `Send test to ${overrideEmail}` : 'Send test to your admin email'}
                      >
                        {sendingId === r.recruiter_id ? <CircleNotch size={10} className="animate-spin" /> : <><PaperPlaneTilt size={11} weight="fill" /> Test</>}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {previewHtml !== null && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPreviewHtml(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-sm">Email preview — {previewName}</h3>
              <button
                onClick={() => setPreviewHtml(null)}
                className="text-xs text-gray-500 hover:text-gray-900"
                data-testid="recruiter-eod-preview-close"
              >Close</button>
            </div>
            <iframe
              title="recruiter-eod-preview"
              srcDoc={previewHtml}
              className="w-full h-[75vh] bg-white"
              data-testid="recruiter-eod-preview-iframe"
              sandbox=""
            />
          </div>
        </div>
      )}
    </div>
  );
}
